<script>
  import { onDestroy, onMount } from "svelte";
  import { filtersConfigStore } from "@stores/damFiltersConfigStores.js";
  import { damfilters } from "lib/stores.js";
  import { getApplicableFilters } from "@utils/getDamApplicableFilters.js";
  // ui-elements
  import { Loading } from "carbon-components-svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import LineChartNeu from "lib/components/LineChartNeu.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import MatrixTable from "./components/MatrixTable.svelte";
  import ButtonNumberInput from "lib/components/ButtonNumberInput.svelte";

  import Filter from "./components/filter/Filter.svelte";
  import AppDrilldownSourceMenu from "./components/AppDrilldownSourceMenu.svelte";

  // sidenav
  import { Accordion, SectionWrapper, Button } from "paxray-components-svelte";
  import AppSelection from "./components/appSelection.svelte";
  import Header from "lib/components/Header.svelte";

  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  import { createRequestScope } from "lib/util/requestScope.js";
  import {
    BOT_WHITE_ICON,
    FULLSCREEN_ICON,
    FULLSCREEN_OFF_ICON,
    TABLE_ICON,
    TIMELINE_ICON,
  } from "@utils/icons.js";

  // Requests der Maske enden, wenn die Maske verlassen wird.
  const abortSignal = createRequestScope();
  export let idproject;
  const { setCurrentView } = filtersConfigStore;

  let selectedApps = [];
  let applicationlist = [];
  let appUserSummary = {};
  let fullscreen = false;
  let selectedSourceIndex = 0;
  let dailySelected = 0;
  let view = "Workloads";
  let paxbotOpen = false;
  let workloadView = "chart";
  let lastLoadKey = "";
  let requestSequence = 0;
  let minFrequency = 5;
  let appliedMinFrequency = 5;
  let frequencyDebounceTimeout;
  let loadError = "";

  let loading = true;
  let data = { summary: {}, apps: [], hourly: {}, daily: {} };
  let activeWorkloadDatasets = [];
  // 0 = wie viele verschiedene Nutzer im Zeitraum, 1 = wie viele gleichzeitig
  let concurrentSelected = 0;
  let workloadTable = { headers: [], rows: [] };

  let lineChart;
  $: setCurrentView(view, "Users");

  $: if (
    $damfilters &&
    $filtersConfigStore.storeLoaded &&
    selectedSourceIndex >= 0
  ) {
    const applicableFilters = getCurrentFilters();
    const loadKey = JSON.stringify({
      idproject,
      source: selectedSourceIndex,
      filters: applicableFilters,
      applications: selectedApps,
    });
    if (loadKey !== lastLoadKey) {
      lastLoadKey = loadKey;
      loadAppUsers(applicableFilters);
    }
  }

  function getCurrentFilters() {
    try {
      return getApplicableFilters($damfilters[idproject]);
    } catch {
      return [];
    }
  }
  onMount(() => {
    if (!lastLoadKey) loadAppUsers(getCurrentFilters());
  });

  $: applicationlist = Array.isArray(data?.apps) ? data.apps : [];
  $: noDataForFilters = !loading && !loadError && applicationlist.length === 0;
  $: appUserSummary = data?.summary || {};
  $: activeWorkloadDatasets = getActiveWorkloadDatasets(
    data,
    dailySelected,
    concurrentSelected,
  );
  $: tableApplications = applicationlist.filter(
    (app) => (Number(app.user_share) || 0) >= appliedMinFrequency,
  );
  $: workloadTable = getAppUserTable(tableApplications);
  $: appUserSummaryItems = [
    {
      label: "Total Users",
      value: formatUserValue(appUserSummary.total_users),
    },
    {
      // Der Mittelwert ueber 24 h stand hier bei 0,0 und half niemandem.
      // Die mittlere Tagesspitze sagt, mit wie vielen gleichzeitigen Nutzern
      // an einem normalen Tag zu rechnen ist.
      label: "Avg. Daily Peak",
      value: formatUserValue(appUserSummary.avg_peaks),
    },
    {
      label: "Avg. Apps",
      value: formatUserValue(appUserSummary.avg_apps_per_day),
    },
    {
      label: "Top10",
      value: `${formatPercent(appUserSummary.top10_coverage)}%`,
    },
  ];

  function dataSource() {
    return selectedSourceIndex === 1 ? "main" : "recordings";
  }

  function getActiveWorkloadDatasets(
    workloadData,
    selectedGranularity,
    selectedConcurrency,
  ) {
    const period =
      workloadData?.[selectedGranularity ? "daily" : "hourly"] || {};
    // "Unique" zaehlt jeden Nutzer einmal je Zeitraum - er war irgendwann da.
    // "Concurrent" zeigt, wie viele im selben Moment aktiv waren, im Mittel
    // und in der Spitze. Zwei Fragen, zwei Antworten, nicht vermischt.
    const reihen = selectedConcurrency
      ? [...(period.avg_users || []), ...(period.peak_users || [])]
      : [...(period.unique_users || [])];
    return reihen.map((dataset) => ({
      ...dataset,
      data: (dataset.data || []).map((point) => ({
        ...point,
        y: Math.round((Number(point.y) || 0) * 10) / 10,
      })),
    }));
  }

  function applyFrequencyDebounced() {
    const nextFrequency = Math.min(100, Math.max(0, Number(minFrequency) || 0));
    clearTimeout(frequencyDebounceTimeout);
    frequencyDebounceTimeout = setTimeout(() => {
      appliedMinFrequency = nextFrequency;
    }, 500);
  }

  onDestroy(() => clearTimeout(frequencyDebounceTimeout));

  function formatUserValue(value) {
    const number = Number(value) || 0;
    return Number.isInteger(number) ? `${number}` : number.toFixed(1);
  }

  function formatPercent(value) {
    const number = Number(value) || 0;
    return number < 10 && number > 0
      ? number.toFixed(1)
      : `${Math.round(number)}`;
  }

  // Seats a concurrent licence model would need, as a share of the named
  // users: 3 of 46 people ever online at once means 7 % of the seats.
  function seatRatio(app) {
    const users = Number(app.total_users) || 0;
    const peak = Number(app.peak_users) || 0;
    return users ? (peak / users) * 100 : 0;
  }

  function getAppUserTable(apps) {
    return {
      headers: [
        "Application",
        "Unique Users",
        "% Unique Users",
        "Top Spike",
        "% Top Spike",
        "Seats",
      ],
      headerTooltips: [
        "",
        "How many different people used this application at all. The number a named-user licence has to cover.",
        "Share of everyone in the project who used this application.",
        "The most people ever active in it at the same moment. The number a concurrent licence has to cover.",
        "The top spike as a share of everyone in the project.",
        "Top spike divided by unique users: the share of seats a concurrent licence model would need. A low value means many people who rarely overlap - room to save. A high value means they all come at once - keep the capacity.",
      ],
      rows: apps.map((app) => [
        app.clearname,
        formatUserValue(app.total_users),
        `${formatPercent(app.user_share)}%`,
        formatUserValue(app.peak_users),
        `${formatPercent(app.peak_users_share)}%`,
        `${formatPercent(seatRatio(app))}%`,
      ]),
    };
  }

  function getRelevantApplications(question = "") {
    const normalizedQuestion = String(question).toLowerCase();
    const visibleApps =
      workloadView === "table" ? tableApplications : applicationlist;
    const sortedApps = [...visibleApps].sort(
      (left, right) =>
        (Number(right.total_users) || 0) - (Number(left.total_users) || 0),
    );
    const relevantApps = sortedApps.filter((app) => {
      const name = app.clearname || app.windowtitle || app.id;
      return normalizedQuestion.includes(String(name).toLowerCase());
    });
    const unique = new Map();
    [...relevantApps, ...sortedApps].forEach((app) => {
      const name = app.clearname || app.windowtitle || app.id;
      if (name && !unique.has(name)) {
        unique.set(name, {
          name,
          totalUsers: Number(app.total_users) || 0,
          userCoverage: `${formatPercent(app.user_share)}%`,
          averageConcurrentUsers: Number(app.avg_users) || 0,
          peakConcurrentUsers: Number(app.peak_users) || 0,
        });
      }
    });
    return [...unique.values()].slice(0, 12);
  }

  function getPaxbotContext(question = "") {
    return {
      page: "App Users",
      projectId: idproject,
      source: dataSource(),
      activeFilterCount: getCurrentFilters().length,
      display: workloadView === "chart" ? "Line chart" : "Table",
      granularity:
        workloadView === "chart"
          ? dailySelected
            ? "Daily"
            : "Hourly"
          : "Overall by application",
      minimumFrequencyPercent:
        workloadView === "table" ? appliedMinFrequency : null,
      selection: {
        level: "Application",
        names: selectedApps,
      },
      globalKpis: appUserSummary,
      relevantApplications: getRelevantApplications(question),
      series: activeWorkloadDatasets.map((dataset) => ({
        name: dataset.label,
        unit: "users",
        points: (dataset.data || []).slice(0, 62).map((point) => ({
          period: point.x,
          value: Math.round((Number(point.y) || 0) * 100) / 100,
        })),
      })),
    };
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      signal: abortSignal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: history.slice(-2),
        context: {
          ...getPaxbotContext(question),
          responseLanguage,
        },
      }),
    });
    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch {
      throw new Error(
        `Paxbot endpoint returned ${response.status || "an invalid response"}`,
      );
    }
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }

  async function loadAppUsers(applicableFilters = getCurrentFilters()) {
    const sequence = ++requestSequence;
    loading = true;
    loadError = "";
    const filterString = JSON.stringify(applicableFilters);
    try {
      const response = await fetchWithSession(
        `/api/damprojects/getAppUserKPIs?project_id=` +
          idproject +
          `&applicationnames=${encodeURIComponent(JSON.stringify(selectedApps))}` +
          `&source=${dataSource()}` +
          `&filter=${encodeURIComponent(filterString)}`,
        { signal: abortSignal },
      );
      if (!response.ok) {
        throw new Error(`App Users request failed (${response.status})`);
      }
      const result = await response.json();
      if (sequence !== requestSequence) return;
      data = result || { summary: {}, apps: [], hourly: {}, daily: {} };
    } catch (error) {
      if (sequence !== requestSequence) return;
      data = { summary: {}, apps: [], hourly: {}, daily: {} };
      loadError = error?.message || "App Users data could not be loaded";
      console.error("Unable to load App Users data", error);
    } finally {
      if (sequence === requestSequence) loading = false;
    }
  }
</script>

<Header
  paddingRight={false}
  section="Usage Analysis"
  area="Usage Analysis"
  breadcrumb="App Users"
  tooltipText="App Users shows unique, average concurrent, and peak concurrent users over time and across applications."
>
  <AppDrilldownSourceMenu bind:selected={selectedSourceIndex} />
  <Filter />
</Header>

<SectionWrapper
  fullscreen={fullscreen && !noDataForFilters}
  fitScreen={true}
  {loading}
  loadingTitle="Loading app usage data."
  loadingHint="Please wait while App Users is prepared."
  emptyState={noDataForFilters}
>
  <div slot="main" class="main_wapper">
    <PaxbotChat
      bind:open={paxbotOpen}
      heading="Pax AI"
      subheading="App Users Assistant"
      requestAnswer={requestPaxbotAnswer}
    />
    <div class="graph_left_toolbar">
      <Button
        kind="secondary"
        icon={workloadView === "chart" ? TABLE_ICON : TIMELINE_ICON}
        iconDescription={workloadView === "chart"
          ? "Show table"
          : "Show line chart"}
        on:click={() =>
          (workloadView = workloadView === "chart" ? "table" : "chart")}
      />
    </div>
    <div class="graph_top_right_toolbar">
      <Button
        kind={paxbotOpen ? "primary" : "secondary"}
        icon={BOT_WHITE_ICON}
        iconDescription="Pax AI"
        on:click={() => (paxbotOpen = !paxbotOpen)}
      />
    </div>
    <div class="canvas">
      <div class="ui_viewer_wrapper">
        {#if loading}
          <div class="loading_wrapper">
            <Loading withOverlay={false} />
          </div>
        {:else if loadError}
          <div class="no_data">{loadError}</div>
        {:else if applicationlist.length > 0}
          {#if workloadView === "chart"}
            {#key dailySelected}
              <LineChartNeu
                bind:this={lineChart}
                height="100%"
                width="100%"
                variant="clean"
                axes={activeWorkloadDatasets.map(() => "y")}
                dataSets={activeWorkloadDatasets}
              />
            {/key}
          {:else}
            <div class="workload_table_wrapper">
              <MatrixTable
                header={workloadTable.headers}
                headerTooltips={workloadTable.headerTooltips || []}
                rows={workloadTable.rows}
                loading={false}
                variant="modern"
                showHeader={false}
              />
            </div>
          {/if}
        {:else}
          <div class="no_data">No matching data for the applied filter</div>
        {/if}
      </div>
    </div>
    <div class="menu_overlay bottom">
      <div class="buttons_wrapper">
        {#if workloadView === "chart"}
          <ContentSwitcher
            bind:selected={dailySelected}
            options={["Hourly", "Daily"]}
            fitContent={true}
          />
          <ContentSwitcher
            bind:selected={concurrentSelected}
            options={["Unique", "Concurrent"]}
            fitContent={true}
          />
        {:else}
          <ButtonNumberInput
            buttonText="Min Frequency (%)"
            bind:value={minFrequency}
            min={0}
            max={100}
            handleMouseDown={applyFrequencyDebounced}
          />
        {/if}
      </div>
      <div class="buttons_wrapper">
        <Button
          kind="secondary"
          icon={fullscreen ? FULLSCREEN_OFF_ICON : FULLSCREEN_ICON}
          on:click={() => {
            fullscreen = !fullscreen;
          }}
        />
      </div>
    </div>
  </div>

  <div slot="emptyState" class="empty_state_text">
    <div class="empty_state_title">
      No data is available for this view with the selected filters.
    </div>
    <div class="empty_state_hint">
      Remove or adjust the filters to update the results.
    </div>
  </div>

  <div class="menu" slot="menu">
    <Accordion
      heading="App Selection"
      open={true}
      tooltipText="Select up to two apps for comparison."
    >
      <AppSelection
        appData={applicationlist}
        bind:selectedApps
        maxSelections={2}
        searchPlaceholder="Search for Apps"
        windowSelectionEnabled={false}
        showKpiSummary
        kpi="user_share"
        shareIsPercentage
        kpiSummaryItems={appUserSummaryItems}
        {loading}
      />
    </Accordion>
  </div>
</SectionWrapper>

<style>
  .main_wapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .main_wapper :global(.paxbot_chat_window) {
    bottom: 5.85rem !important;
    position: absolute !important;
    right: 1rem !important;
    z-index: 50 !important;
  }

  .main_wapper :global(.paxbot_chat_mascot) {
    bottom: -0.65rem !important;
    position: absolute !important;
    right: 1.55rem !important;
    z-index: 51 !important;
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bottom {
    bottom: 0;
  }

  .canvas {
    position: absolute;
    top: 3.75rem;
    bottom: 3.75rem;
    height: auto;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .graph_top_right_toolbar {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    z-index: 4;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
  }

  .graph_left_toolbar {
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    z-index: 4;
    display: flex;
    align-items: center;
  }

  .graph_left_toolbar :global(.button_wrapper) {
    height: 3rem;
    width: 3rem;
  }

  .graph_left_toolbar :global(img) {
    filter: brightness(0) invert(1);
    height: 1.25rem;
    object-fit: contain;
    width: 1.25rem;
  }

  .buttons_wrapper {
    display: flex;
    gap: 0.5rem;
    user-select: none;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .ui_viewer_wrapper {
    height: 100%;
    width: 100%;
  }

  .workload_table_wrapper {
    width: 100%;
    height: 100%;
    padding: 0.5rem 1rem;
    box-sizing: border-box;
    overflow: hidden;
  }

  .workload_table_wrapper :global(.table-container.modern) {
    height: 100%;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }

  .no_data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    font-size: 2rem;
    color: var(--pax-black);
  }

  .empty_state_title,
  .empty_state_hint {
    font-size: 1rem;
  }

  .empty_state_title {
    color: var(--pax-black);
  }

  .empty_state_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }
</style>
